import { SimpleTable, TableSkeleton, DrilldownCell, Alert } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import SingleValueDisplay from '@/insights/components/SingleValueDisplay'
import {
  ApiService,
  InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatDurationLabel, formatDurationToHours, formatNumberLocale } from '@/utils/formatting'

import HorizontalChart from './HorizontalChart'

export const DurationOptions = [
  { label: '3 Days', value: '3d' },
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d', isDefault: true },
  { label: 'Custom', value: 'custom' },
]

export const DurationUnits = [
  { label: 'Hours', value: 'h' },
  { label: 'Days', value: 'd', isDefault: true },
]

export interface StationaryAssetCountParams extends InsightParametersType {
  companyIds?: number[]
  geofenceIds?: string[]
  geofenceCategoryIds?: string[]
  minDuration: string
  customMinDuration?: string
  displayFormat: string
}

export interface StationaryAssetCountData {
  companyId: number
  companyName: string
  assetCount: number
}

const withMinimumDuration = (parameters: StationaryAssetCountParams) => ({
  ...parameters,
  minimumDuration: formatDurationToHours(
    (parameters.minDuration === 'custom' ? parameters.customMinDuration : parameters.minDuration) ?? ''
  ),
})

const StationaryAssetCount: InsightDefinition<StationaryAssetCountParams, StationaryAssetCountData[]> = {
  active: true,
  slug: 'utilization/count-by-stationary-asset',
  category: 'utilization',
  name: 'Stationary Asset Count',
  description: 'Display stationary asset counts',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      if (field === 'minDuration' && parameters.minDuration !== 'custom') {
        return { ...parameters, customMinDuration: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyIds', control: 'CompanyDropdown', required: true, isMultiSelect: true },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'minDuration',
        label: 'Minimum Duration',
        control: 'CustomSingleSelect',
        options: DurationOptions,
      },
      {
        id: 'customMinDuration',
        label: 'Enter duration',
        control: 'CustomNumberInput',
        dependsOn: 'minDuration',
        dependsOnValue: 'custom',
        defaultValue: 30,
        // max: 30,
        options: DurationUnits,
      },
      {
        id: 'displayFormat',
        control: 'CustomSingleSelect',
        options: [
          { label: 'Table', value: 'table', isDefault: true },
          { label: 'Single Value Chart', value: 'single' },
          { label: 'Horizontal Bar Chart', value: 'horizontal' },
        ],
      },
    ],

    isValid: (configuration: StationaryAssetCountParams) => {
      if (configuration.minDuration === 'custom') {
        const customMinDuration = parseInt(configuration.customMinDuration ?? '0', 10)

        if (!customMinDuration || customMinDuration === 0) {
          return 'minimum duration is required.'
        }
        if (customMinDuration > 30) {
          return 'minimum duration cannot exceed 30 days.'
        }
      }

      const hasGeofences = Array.isArray(configuration.geofenceIds) && configuration.geofenceIds.length > 0
      const hasCategories =
        Array.isArray(configuration.geofenceCategoryIds) && configuration.geofenceCategoryIds.length > 0

      if (!hasGeofences && !hasCategories) {
        return 'Please select at least one Geofence or Geofence Category.'
      }

      if (Array.isArray(configuration?.companyIds) && configuration.companyIds.length > 0) {
        return true
      } else {
        return 'Company is required.'
      }
    },
  },

  load: async (api: ApiService, rawParameters: StationaryAssetCountParams) => {
    const path = `/insights/utilization/stationary-asset-count?${buildQuery(withMinimumDuration(rawParameters))}`

    const response = await api.get<StationaryAssetCountData[]>({
      path,
      origin: 'StationaryAssetCount#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: StationaryAssetCountParams, title: string) => {
    const path = '/insights/utilization/stationary-asset-count'
    await downloadCsv(api, path, withMinimumDuration(parameters), title)
  },

  Display: ({ status, configuration, data }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    const parameters = configuration.parameters as StationaryAssetCountParams
    const isLoadingOrDisplaying =
      status.code === InsightStatusCode.DISPLAYING || status.code === InsightStatusCode.LOADING
    if (showSkeleton && isLoadingOrDisplaying) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={2} withHeader rowHeight={35} />
    }
    if (!isLoadingOrDisplaying) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alerts found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    if (parameters.displayFormat?.startsWith('single')) {
      const ddConfig: InsightConfigurationType = {
        ...configuration,
        slug: 'utilization/count-by-stationary-asset',
        title: `${configuration.title}: Table View`,
        parameters: {
          ...parameters,
          displayFormat: 'table',
          isDrilldown: true,
        },
      }
      const value = (data || []).reduce((sum, row) => sum + row.assetCount, 0)
      return <SingleValueDisplay value={value} width={configuration.width} drilldownConfiguration={ddConfig} />
    }

    const sortedDisplayData = [...data].sort((a, b) => {
      const aValue = Number(a.assetCount || 0)
      const bValue = Number(b.assetCount || 0)

      return bValue - aValue
    })

    if (parameters.displayFormat?.startsWith('horizontal')) {
      return <HorizontalChart data={sortedDisplayData ?? []} configuration={configuration} />
    }

    // Create table body for the new SimpleTable format
    const body = sortedDisplayData?.map((item) => {
      const ddConfig: InsightConfigurationType = {
        ...configuration,
        slug: 'utilization/stationary-assets-detail',
        title: `${configuration.title}: Details - ${item.companyName}`,
        parameters: {
          ...parameters,
          companyId: item.companyId,
          companyIds: undefined,
          displayFormat: 'table',
          isDrilldown: true,
        },
      }
      const result: Record<string, string | DrilldownCell | React.ReactNode> = {
        Company: <span className="flex pl-1">{item.companyName}</span>,
        'Asset Count':
          Number(item.assetCount) > 0 ? (
            {
              value: formatNumberLocale(Number(item.assetCount)),
              className: 'block pr-5 w-full text-center',
              configuration: ddConfig,
              id: `${item.companyId}-assetCount`,
            }
          ) : (
            <span className="block pr-5 w-full text-center">{formatNumberLocale(Number(item.assetCount))}</span>
          ),
      }

      return result
    })

    const headers = ['Company', 'Asset Count']

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as StationaryAssetCountParams
    if (params.companyIds) {
      const companyList = companies.filter((c) => params.companyIds?.includes(c.id))
      if (companyList.length) {
        fields['Company'] = companyList.map((c) => c.name).join(', ')
      }
    }
    if (params.minDuration) {
      const duration = params.minDuration === 'custom' ? params.customMinDuration : params.minDuration
      fields['Minimum Duration'] = formatDurationLabel(duration || '')
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default StationaryAssetCount
